import React from 'react';
import { Book, MessageSquare, Globe, User, Heart, BookOpen } from 'lucide-react';

export const AboutPage = () => {
  return (
    <div className="container mx-auto py-20 px-4 max-w-7xl">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-['Playfair_Display']">Our Mission</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Bridging language barriers to spread theological wisdom and spiritual teachings across the globe.
        </p>
      </div>

      {/* Mission Statement */}
      <div className="mb-20">
        <div className="flex flex-col md:flex-row gap-10 items-center">
          <div className="md:w-1/2">
            <div className="rounded-2xl overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 aspect-video flex items-center justify-center">
              <BookOpen className="h-20 w-20 text-white opacity-80" />
            </div>
          </div>
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-4 font-['Playfair_Display']">Why We Exist</h2>
            <p className="text-lg text-gray-600 mb-4">
              Divine Echo was born from a simple observation: while the message of faith is universal, language barriers often limit its reach. We believe that theological wisdom and spiritual teachings should be accessible to everyone, regardless of the language they speak.
            </p>
            <p className="text-lg text-gray-600 mb-6">
              Our sophisticated AI technology preserves the nuance, context, and spiritual essence of sacred texts and theological works during translation and vocalization, ensuring that nothing is lost in translation.
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white text-lg rounded-lg">
              Learn More About Our Technology
            </button>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center font-['Playfair_Display']">Our Core Values</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Book className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Theological Accuracy</h3>
            <p className="text-gray-600">We understand the importance of precision in theological content, employing experts to ensure doctrinal integrity in all translations.</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Globe className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Global Accessibility</h3>
            <p className="text-gray-600">We're committed to making spiritual wisdom accessible across language barriers, reaching communities worldwide.</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Heart className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Faithful Innovation</h3>
            <p className="text-gray-600">We employ cutting-edge technology while remaining deeply respectful of traditional teachings and sacred texts.</p>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-4 text-center font-['Playfair_Display']">Our Team</h2>
        <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
          Divine Echo brings together theologians, linguists, voice artists, and AI specialists who share a passion for making sacred wisdom universally accessible.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto mb-4 flex items-center justify-center">
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="font-semibold">Dr. Sarah Mitchell</h3>
            <p className="text-sm text-gray-600">Biblical Scholar & Founder</p>
          </div>
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto mb-4 flex items-center justify-center">
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="font-semibold">Reverend James Chen</h3>
            <p className="text-sm text-gray-600">Theological Advisor</p>
          </div>
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto mb-4 flex items-center justify-center">
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="font-semibold">Dr. Maya Patel</h3>
            <p className="text-sm text-gray-600">AI & Language Director</p>
          </div>
          <div className="text-center">
            <div className="w-32 h-32 rounded-full bg-gray-200 mx-auto mb-4 flex items-center justify-center">
              <User className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="font-semibold">Thomas Rodriguez</h3>
            <p className="text-sm text-gray-600">Voice Technology Lead</p>
          </div>
        </div>
      </div>

      {/* Impact Stories */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center font-['Playfair_Display']">Our Impact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-white p-8 rounded-xl border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">Rural Ministry Outreach</h3>
            <p className="text-gray-600 mb-4">
              "Divine Echo has transformed our ability to reach remote villages with translated sermons. Communities that previously had no access to theological materials in their native language are now engaging deeply with our teachings."
            </p>
            <p className="font-semibold">- International Missions Alliance</p>
          </div>
          <div className="bg-white p-8 rounded-xl border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">Seminary Education</h3>
            <p className="text-gray-600 mb-4">
              "We've incorporated Divine Echo into our curriculum, allowing students to access ancient theological texts in their modern language while preserving the nuance and context of the original writings."
            </p>
            <p className="font-semibold">- Global Theological Seminary</p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 p-10 text-white text-center">
        <h2 className="text-3xl font-bold mb-4 font-['Playfair_Display']">Join Our Mission</h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Whether you're a religious leader, translator, or technologist, there are many ways to contribute to making theological wisdom universally accessible.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="px-4 py-2 bg-white/10 text-white border border-white/20 hover:bg-white/20 rounded-lg flex items-center">
            <MessageSquare className="mr-2 h-4 w-4" />
            Contact Us
          </button>
          <button className="px-4 py-2 bg-white text-purple-800 hover:bg-white/90 rounded-lg">
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage; 